import { LegalLayout } from "./LegalLayout";
import { SOCLOSE } from "../../constants/soclose";

const PRODUCT = "3DPrintForge";

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updatedAt="2025-12-16">
      <p>
        This Privacy Policy explains how <strong>{SOCLOSE.name}</strong> ({SOCLOSE.uenLabel}, registered in Singapore,
        hereafter <em>"SoClose"</em>, <em>"we"</em>, <em>"us"</em>, <em>"our"</em>) collects, uses, and protects your
        personal data when you use <strong>{PRODUCT}</strong> and any related service we provide.
      </p>
      <p>
        We comply with the <strong>Singapore Personal Data Protection Act 2012 (PDPA)</strong> and the regulations issued
        by the Personal Data Protection Commission (PDPC). If you are based in the EU/EEA, the GDPR may also apply --
        equivalent rights are honored.
      </p>

      <h2>1. Data we collect</h2>
      <p>Depending on your use of the service, we may collect:</p>
      <ul>
        <li><strong>Account data</strong>: name, email, password (hashed), profile preferences.</li>
        <li><strong>Usage data</strong>: pages visited, features used, anonymized analytics (only with consent).</li>
        <li><strong>Technical data</strong>: IP address, browser, device, language -- used for security and fraud prevention.</li>
        <li><strong>Billing data</strong>: name, billing address, last 4 digits of card. Full card data is never stored on our servers (processed by Stripe / Helio).</li>
        <li><strong>Content data</strong>: 3D models, meshes, prompts and files you upload or generate, used solely to deliver the service.</li>
      </ul>

      <h2>2. Why we use it (lawful purposes)</h2>
      <ul>
        <li>Operate, maintain and secure the service.</li>
        <li>Authenticate you and prevent abuse.</li>
        <li>Process payments and issue invoices.</li>
        <li>Improve the product based on aggregated, anonymized usage data.</li>
        <li>Send transactional emails (account actions, receipts). Marketing emails only with your explicit opt-in.</li>
        <li>Comply with Singapore tax, accounting, and regulatory obligations.</li>
      </ul>

      <h2>3. Sharing your data</h2>
      <p>We never sell your personal data. We share strictly the minimum necessary with:</p>
      <ul>
        <li><strong>Sub-processors</strong> -- payment (Stripe, Helio), email (Resend), hosting (our private VPS), analytics (only if you opt in).</li>
        <li><strong>Authorities</strong> -- if legally compelled by a Singapore court order or equivalent.</li>
      </ul>

      <h2>4. International transfers</h2>
      <p>
        Our infrastructure is hosted on a dedicated server. Some sub-processors operate outside Singapore. When data is
        transferred internationally, we ensure a comparable standard of protection through contractual safeguards, in
        accordance with section 26 of the PDPA.
      </p>

      <h2>5. Retention</h2>
      <p>
        We retain personal data only as long as necessary for the purposes above, then delete or anonymize it. Account
        data is kept while your account is active. Billing records are retained for at least 5 years (Singapore tax law).
        You may request earlier deletion -- see section 7.
      </p>

      <h2>6. Security</h2>
      <p>
        We apply reasonable technical and organizational safeguards: TLS 1.2+ in transit, encrypted secrets at rest, IP
        allow-lists and fail2ban on infrastructure, role-based access, audited logs, regular backups, and a documented
        incident response process. No system is perfectly secure -- we will notify you and the PDPC of any material
        breach as required by the PDPA.
      </p>

      <h2>7. Your rights under PDPA</h2>
      <p>You may request, at any time, to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Correct inaccurate or incomplete data.</li>
        <li>Withdraw consent (where processing relies on it).</li>
        <li>Have your data deleted, subject to legal retention obligations.</li>
        <li>Receive a copy of your data in a portable format.</li>
      </ul>
      <p>
        Submit any request to <a href={`mailto:${SOCLOSE.contact.email}`}>{SOCLOSE.contact.email}</a>. We respond within
        30 days. If you are not satisfied, you may lodge a complaint with the PDPC:{" "}
        <a href={SOCLOSE.privacy.authorityUrl} target="_blank" rel="noopener noreferrer">pdpc.gov.sg</a>.
      </p>

      <h2>8. Cookies</h2>
      <p>
        We use a granular cookie banner -- essential cookies are always on, analytics and marketing cookies require your
        explicit consent. See the <a href="/cookies">Cookie Policy</a> for the full list.
      </p>

      <h2>9. Changes to this Policy</h2>
      <p>
        We may update this Policy. The "Last updated" date at the top reflects the latest revision. Material changes will
        be communicated by email or via an in-app notice.
      </p>

      <h2>10. Data Protection Officer / Contact</h2>
      <address className="legal-address">
        {SOCLOSE.name}<br />
        {SOCLOSE.address.line1}<br />
        {SOCLOSE.address.line2}<br />
        {SOCLOSE.address.country} {SOCLOSE.address.postal}<br />
        Email: <a href={`mailto:${SOCLOSE.contact.email}`}>{SOCLOSE.contact.email}</a><br />
        Phone: <a href={`tel:${SOCLOSE.contact.phoneRaw}`}>{SOCLOSE.contact.phone}</a>
      </address>
    </LegalLayout>
  );
}
